/* eslint-disable indent */
import React, { useEffect } from 'react';
import { FormData, FormErrors, IUpsertActivityLogDialog } from './interfaces';
import {
  AppBar,
  Button,
  CustomTheme,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  MenuItem,
  SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { SelectField } from '../../../../components/forms/SelectField';
import { atLeast, isEmpty } from '../../../../helpers/formHelpers';

import { LoadingLayover } from '../../../../components/LoadingLayover';
import { enqueueSnackbar } from 'notistack';
import { useGetAgentsList } from '../../../../reactQuery/Agents/agents';
import { useNavigate } from 'react-router';
import { MutateOptions } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useFormState from '../../../../hooks/useFormState';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import {
  useCreatActivityLogtMutation,
  useGetActions,
  useUpdateActivityLogMutation,
} from '../../../../reactQuery/Activities/activities';
import { useGetClientsList } from '../../../../reactQuery/clients';
import { TagsField } from '../../../../components/forms/TagsField';
import { DateTimePicker } from '../../../../components/forms/DateTimePicker';
import dayjs, { Dayjs } from 'dayjs';
import { isTodayOrPast, isValidDate } from '../../../../helpers/dateHelpers';
import { useAuth } from '../../../../context/AuthProvider';
import {
  useGetClientContactsList,
  useGetContactsList,
} from '../../../../reactQuery/contacts';
import { ROUTES } from '../../../../routes/Routes';
import { CheckboxOption } from '../../../../components/forms';

export const UpsertActivityLogDialog: React.FC<IUpsertActivityLogDialog> = ({
  activityLog,
  show,
  onClose,
}): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { auth } = useAuth();
  const currentUser = auth?.user;

  const { mutate: createActivityLog, isLoading: isCreatingActivityLog } =
    useCreatActivityLogtMutation();
  const { mutate: updateActivityLog, isLoading: isUpdatingActivityLog } =
    useUpdateActivityLogMutation();

  const theme = useTheme<CustomTheme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('tablet'));
  const isEditMode = !!activityLog;

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges<FormData>(formData);

  const {
    data: agentsList,
    isLoading: loadingAgentsList,
    error: agentsListError,
  } = useGetAgentsList(show);

  const {
    data: clientsList,
    isLoading: loadingClientsList,
    error: clientsListError,
  } = useGetClientsList();

  const { data: contactsAsList, error: contactsListError } = useGetContactsList(
    !!activityLog
  );

  const { data: contactsOfClientList, error: contactsOfClientListError } =
    useGetClientContactsList(formData.clientId, !activityLog);

  const contactsList = activityLog ? contactsAsList : contactsOfClientList;

  const {
    data: actionsList,
    isLoading: loadingActionsList,
    error: actionsListError,
  } = useGetActions();

  const isLoadingListOptions =
    loadingAgentsList || loadingClientsList || loadingActionsList;

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.summary || isEmpty(formData.summary)) {
      errors.summary = t('errors.required');
    } else if (!atLeast(formData.summary, 10)) {
      errors.summary = t('errors.atLeast', {
        field: t('entities:activityLog.summary').toLowerCase(),
        min: 10,
      });
    }

    if (formData.performedAt === null || formData.performedAt === undefined) {
      errors.performedAt = t('errors.required');
    } else if (!isValidDate(formData.performedAt)) {
      errors.performedAt = t('errors.invalidDate');
    } else if (!isTodayOrPast(formData.performedAt)) {
      errors.performedAt = t('errors.dateNeedsToBeTodayOrInThePast');
    }

    if (formData.clientId === undefined) {
      errors.clientId = t('errors.pleaseSelectAnOption');
    }

    if (formData.actionTags === undefined) {
      errors.actionTags = t('errors.pleaseSelectAnOption');
    }

    if (formData.agentId === undefined) {
      errors.agentId = t('errors.pleaseSelectAnOption');
    }

    setFormErrors(errors);
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!Object.keys(formErrors).length) {
      const activityLogData = {
        summary: formData.summary as string,
        clientId: formData.clientId as number,
        actionId: formData.actionTags?.length ? formData.actionTags[0] : 0,
        agentId: formData.agentId as number,
        performedAt: formData.performedAt as Dayjs,
        contacts: (formData.contacts || []) as number[],
        excludeFromContactInterval: formData.excludeFromContactInterval
          ? formData.excludeFromContactInterval
          : false,
      };

      const mutateOptions:
        | MutateOptions<number, AxiosError, unknown>
        | undefined = {
        onSuccess: (activityLogId) => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateActivityLog.success')
              : t('forms:createActivityLog.success'),
            {
              variant: 'success',
            }
          );

          navigate(ROUTES.activityLog.details(activityLogId));
          onClose();
        },
        onError: () => {
          enqueueSnackbar(
            isEditMode
              ? t('forms:updateActivityLog.failed')
              : t('forms:createActivityLog.failed'),
            {
              variant: 'error',
            }
          );
        },
      };

      if (isEditMode) {
        updateActivityLog(
          {
            activityLogId: activityLog.id,
            activityLogData,
          },
          mutateOptions
        );
      } else {
        createActivityLog(activityLogData, mutateOptions);
      }
    }
  };

  useEffect(() => {
    const error =
      actionsListError ||
      agentsListError ||
      clientsListError ||
      contactsListError ||
      contactsOfClientListError;

    if (error) {
      enqueueSnackbar(t('errors.somethingWentWrong'), {
        variant: 'error',
      });

      onClose();
    }
  }, [agentsListError]);

  useEffect(() => {
    setFormErrors({});

    if (activityLog) {
      setFormData({
        performedAt: dayjs(activityLog.performedAt),
        clientId: activityLog.client.id,
        actionTags: [activityLog.action.id],
        agentId: activityLog.agent.id,
        summary: activityLog.summary,
        contacts: activityLog.contacts?.map((contact) => contact.id),
        excludeFromContactInterval: activityLog.excludeFromContactInterval,
      });
    } else {
      setFormData({
        performedAt: dayjs(),
        agentId: currentUser?.id,
        excludeFromContactInterval: true,
      });
    }
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      keepMounted={false}
      //   TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            {isEditMode
              ? t('forms:updateActivityLog.title')
              : t('forms:createActivityLog.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            disabled={isCreatingActivityLog || isUpdatingActivityLog}
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: fullScreen ? undefined : '400px',
        }}
      >
        {isLoadingListOptions ? (
          <LoadingLayover height="200px" />
        ) : (
          <Stack
            gap="15px"
            component="form"
            id="createActivityLogForm"
            onSubmit={handleSubmit}
          >
            <DateTimePicker
              label={t('entities:common.date')}
              onChange={(date) => setFormFieldData('performedAt', date)}
              selectedDateTime={formData.performedAt}
              error={!!formErrors.performedAt}
              errorMessage={formErrors.performedAt}
              disableFuture
            />

            <SelectField
              name="client"
              label={t('entities:activityLog.client')}
              value={formData.clientId ? String(formData.clientId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('clientId', Number(event.target.value))
              }
              error={!!formErrors.clientId}
              errorMessage={formErrors.clientId}
              required
            >
              {clientsList?.map((client) => (
                <MenuItem key={client.id} value={client.id}>
                  {client.name}
                </MenuItem>
              ))}
            </SelectField>

            <TagsField
              existingTags={contactsList?.map((contact) => {
                return {
                  id: contact.id,
                  name: `${contact.firstName} ${contact.lastName}`,
                };
              })}
              label={t('entities:activityLog.contacts')}
              value={formData.contacts || []}
              error={formErrors.contacts}
              disabled={!(contactsList && contactsList.length > 0)}
              onChange={(value) => {
                setFormFieldData('contacts', value);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
            />

            <TagsField
              existingTags={actionsList}
              label={t('entities:activityLog.action')}
              value={formData.actionTags || []}
              onChange={(value) => {
                setFormFieldData('actionTags', value);
              }}
              sx={{
                maxWidth: fullScreen ? undefined : '400px',
              }}
              maxTags={1}
              createTags
              error={formErrors.actionTags}
              required
            />

            <SelectField
              name="agent"
              label={t('entities:activityLog.agent')}
              value={formData.agentId ? String(formData.agentId) : ''}
              onChange={(event: SelectChangeEvent) =>
                setFormFieldData('agentId', Number(event.target.value))
              }
              error={!!formErrors.agentId}
              errorMessage={formErrors.agentId}
              required
            >
              {agentsList?.map((agent) => (
                <MenuItem key={agent.id} value={agent.id}>
                  {`${agent.firstName} ${agent.lastName}`}
                </MenuItem>
              ))}
            </SelectField>

            <TextField
              label={t('entities:activityLog.summary')}
              variant="outlined"
              size="small"
              value={formData.summary}
              placeholder={activityLog?.summary}
              onChange={(e) => setFormFieldData('summary', e.target.value)}
              error={!!formErrors.summary}
              helperText={formErrors.summary}
              multiline
              rows={4}
              required
            />

            <CheckboxOption
              label={t('entities:activityLog.excludeFromContactInterval')}
              value={formData.excludeFromContactInterval ? 1 : 0}
              checked={formData.excludeFromContactInterval}
              onChange={(value) =>
                setFormFieldData('excludeFromContactInterval', value)
              }
            />
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isCreatingActivityLog || isUpdatingActivityLog}
        >
          {t('buttons.cancel')}
        </Button>
        <Button
          form="createActivityLogForm"
          type="submit"
          variant="contained"
          onClick={validateForm}
          disabled={
            isLoadingListOptions ||
            isCreatingActivityLog ||
            isUpdatingActivityLog
          }
        >
          {isEditMode ? t('buttons.update') : t('buttons.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
